import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { clientsAPI } from '../../services/api';
import { 
  Lock, 
  User, 
  Eye, 
  EyeOff, 
  ShieldCheck, 
  Truck, 
  Building,
  KeyRound, 
  Key,
  AlertTriangle,
  ArrowLeft,
  Smartphone,
  Check,
  Sparkles
} from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import BrandLogo from '../../components/BrandLogo';
import loginBg from '../../assets/loginbg.png';

export default function LoginPage() {
  const [selectedPortal, setSelectedPortal] = useState('client'); // 'client' | 'vendor'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [activeSessionError, setActiveSessionError] = useState(null);
  const [tenantConfigError, setTenantConfigError] = useState(null);
  const [tenantInfo, setTenantInfo] = useState(null);

  // 2FA State
  const [twoFactorData, setTwoFactorData] = useState(null); // { tempToken, email, name, isSuperAdmin }
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [isVerifying2FA, setIsVerifying2FA] = useState(false);
  
  const { login, verify2FA, switchPortal } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const tenantParam = queryParams.get('tenant') || queryParams.get('t') || queryParams.get('client_code');
  const portalParam = queryParams.get('portal');

  useEffect(() => {
    if (portalParam === 'vendor' || portalParam === 'client') {
      setSelectedPortal(portalParam);
    }
  }, [portalParam]);

  useEffect(() => {
    if (tenantParam) {
      clientsAPI.getPublicBranding(tenantParam)
        .then(res => {
          if (res.data?.success && res.data?.tenant) {
            setTenantInfo(res.data.tenant);
          } else {
            setTenantInfo(null);
          }
        })
        .catch(() => setTenantInfo(null));
    }
  }, [tenantParam]);

  const handleSelectPortal = (portal) => {
    setSelectedPortal(portal);
    setActiveSessionError(null);
    setTenantConfigError(null);
  };

  const handleForceLogin = async () => {
    setIsLoading(true);
    setActiveSessionError(null);
    setTenantConfigError(null);
    try {
      const res = await login(email, password, rememberMe, true);
      if (res?.data?.requires2FA) {
        setTwoFactorData(res.data);
        toast.success('Security Verification: Enter your 6-digit code.');
        return;
      }

      const u = res?.data?.user || res?.data;
      const roles = Array.isArray(u?.role) ? u.role : (Array.isArray(u?.roles) ? u.roles.map(r => r?.name || r) : [u?.role].filter(Boolean));
      const isSuper = u?.type === 'SuperAdmin' || roles.includes('SuperAdmin') || u?.email === 'connect@sanglob.com' || u?.email === 'superadmin@sanglob.local';

      if (isSuper) {
        toast.success('SuperAdmin authenticated. Entering Platform Control Tower.');
        navigate('/superadmin/control-tower', { replace: true });
        return;
      }
      
      const isVendorUser = u?.portal_access === 'Vendor Portal' || u?.type === 'Vendor' || (selectedPortal === 'vendor' && !String(u?.portal_access || '').toLowerCase().includes('client'));
      const targetPortal = isVendorUser ? 'vendor' : 'client';
      switchPortal(targetPortal);
      localStorage.setItem('activePortal', targetPortal);
      toast.success('Session override accepted. Welcome back!');

      if (targetPortal === 'vendor') {
        navigate('/vendor/dashboard', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    } catch (error) {
      console.error('Force login error:', error);
      toast.error('Failed to force login. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setActiveSessionError(null);
    setTenantConfigError(null);
    
    if (!email || !password) {
      toast.error('Please enter both email/username and password');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const res = await login(email, password, rememberMe, false, twoFactorCode.trim());
      if (res?.data?.requires2FA) {
        setTwoFactorData(res.data);
        toast.success('Security Verification Required');
        return;
      }

      const u = res?.data?.user || res?.data;
      const roles = Array.isArray(u?.role) ? u.role : (Array.isArray(u?.roles) ? u.roles.map(r => r?.name || r) : [u?.role].filter(Boolean));
      const isSuper = u?.type === 'SuperAdmin' || roles.includes('SuperAdmin') || u?.email === 'connect@sanglob.com' || u?.email === 'superadmin@sanglob.local';

      if (isSuper) {
        toast.success('Welcome SuperAdmin! Entering Platform Control Tower.');
        navigate('/superadmin/control-tower', { replace: true });
        return;
      }

      const isVendorUser = u?.portal_access === 'Vendor Portal' || u?.type === 'Vendor' || (selectedPortal === 'vendor' && !String(u?.portal_access || '').toLowerCase().includes('client'));
      const targetPortal = isVendorUser ? 'vendor' : 'client';
      switchPortal(targetPortal);
      localStorage.setItem('activePortal', targetPortal);
      toast.success(`Welcome back! Entering ${targetPortal === 'vendor' ? 'Vendor Portal' : 'Client Workspace'}.`);
      
      const savedPath = localStorage.getItem('last_visited_path');
      const fromPath = location.state?.from ? (location.state.from.pathname + (location.state.from.search || '')) : null;
      const targetDestination = fromPath || (savedPath && !savedPath.startsWith('/login') && !savedPath.startsWith('/superadmin/login') ? savedPath : (targetPortal === 'vendor' ? '/vendor/dashboard' : '/dashboard'));
      
      navigate(targetDestination, { replace: true });
    } catch (error) {
      if (error.response?.data?.code === 'ACTIVE_SESSION_EXISTS') {
        setActiveSessionError({
          message: error.response.data.message || 'Another session is active with this account.',
          detail: 'Click below to terminate previous sessions and enter your portal.'
        });
        return;
      }

      if (
        error.response?.data?.code === 'TENANT_CONFIG_REQUIRED' || 
        error.response?.data?.code === 'LICENSE_REQUIRED' || 
        error.response?.data?.code === 'SUBSCRIPTION_REQUIRED' ||
        error.response?.data?.code === 'TENANT_INACTIVE'
      ) {
        setTenantConfigError(error.response.data.message || 'Tenant configuration incomplete. Contact Platform SuperAdmin to configure your client license and subscription.');
        return;
      }

      if (error.response?.data?.code === 'LICENSE_EXPIRED' && error.response.data.renew_url) {
        toast.error('Subscription has expired. Redirecting to marketplace...');
        setTimeout(() => {
          window.location.href = error.response.data.renew_url;
        }, 2000);
        return;
      }

      const errorMessage = error.response?.data?.message || 'Authentication failed. Please verify credentials.';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    if (!twoFactorCode || twoFactorCode.trim().length === 0) {
      toast.error('Please enter the 6-digit verification code or backup code');
      return;
    }

    setIsVerifying2FA(true);
    try {
      const res = await verify2FA(twoFactorData.tempToken, twoFactorCode.trim(), rememberMe);
      const u = res?.data?.user || res?.data;
      const roles = Array.isArray(u?.role) ? u.role : (Array.isArray(u?.roles) ? u.roles.map(r => r?.name || r) : [u?.role].filter(Boolean));
      const isSuper = u?.type === 'SuperAdmin' || roles.includes('SuperAdmin') || u?.email === 'connect@sanglob.com' || u?.email === 'superadmin@sanglob.local';

      if (isSuper) {
        toast.success('Two-Factor Authentication verified. Entering Control Tower.');
        navigate('/superadmin/control-tower', { replace: true });
        return;
      }

      switchPortal(selectedPortal);
      localStorage.setItem('activePortal', selectedPortal);
      toast.success(`Identity Verified. Welcome back!`);

      if (selectedPortal === 'vendor' || u?.portal_access === 'Vendor Portal' || u?.type === 'Vendor') {
        navigate('/vendor/dashboard', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid verification code. Please try again.');
    } finally {
      setIsVerifying2FA(false);
    }
  };

  const handleSSO = (provider) => {
    toast.info(`${provider} Single Sign-On initiated. Connecting to identity provider...`);
  };

  const isVendor = selectedPortal === 'vendor';

  return (
    <div className="min-h-screen w-full flex flex-col justify-between font-sans bg-slate-950 text-slate-900 selection:bg-blue-600 selection:text-white overflow-x-hidden relative">
      
      {/* 1. TOP ROYAL BLUE HEADER BAR */}
      <header className="w-full bg-[#1e40af] bg-gradient-to-r from-blue-900 via-blue-800 to-indigo-900 border-b border-blue-700/60 px-6 sm:px-10 py-2.5 flex items-center justify-between z-20 shadow-md shrink-0">
        <div className="flex items-center gap-3">
          <BrandLogo className="h-7 sm:h-8" dark={true} />
          <div className="hidden sm:flex items-center gap-2.5 pl-3 border-l border-blue-400/30">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-blue-950/40 text-blue-100 text-xs font-semibold border border-blue-400/30 shadow-2xs backdrop-blur-xs">
              <Sparkles className="w-3.5 h-3.5 text-cyan-300" />
              <span>Enterprise Platform v2.6</span>
            </span>
          </div>
        </div>

        <div className="flex items-center">
          <span className="text-[11.5px] sm:text-xs md:text-[13px] font-medium tracking-wide text-blue-100/90 text-right select-none">
            Modern Intelligent Resource Enterprise & Value Analytics
          </span>
        </div>
      </header>

      {/* 2. MIDDLE SECTION: BACKGROUND IMAGE WITH RIGHT-SIDE FADE & FLOATING LOGIN CARD */}
      <main className="flex-1 w-full relative min-h-[calc(100vh-105px)] overflow-hidden flex items-center justify-end">
        
        {/* Background Artwork Layer */}
        <div className="absolute inset-0 z-0 select-none pointer-events-none">
          <img 
            src={loginBg} 
            alt="MIREVA — Intelligent Business Management" 
            className="w-full h-full object-cover object-left lg:object-center"
          />
          {/* Smooth Right-Side Fade Overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-slate-950/15 to-slate-950/45" />
          <div className="absolute right-0 top-0 bottom-0 w-full sm:w-2/3 lg:w-1/2 xl:w-2/5 bg-gradient-to-l from-slate-950/60 via-slate-950/25 to-transparent backdrop-blur-[1px]" />
        </div>

        {/* Floating Right-Aligned Login Card */}
        <div className="relative z-10 w-full max-w-[1600px] mx-auto px-4 sm:px-8 lg:px-14 xl:px-20 py-6 sm:py-8 pb-16 flex items-center justify-center lg:justify-end">
          <div className="w-full max-w-[430px] sm:max-w-[450px] lg:max-w-[440px] xl:max-w-[460px] bg-white/95 backdrop-blur-2xl rounded-[28px] sm:rounded-[32px] border border-white/80 shadow-2xl shadow-blue-950/50 p-6 sm:p-8 text-slate-900 transition-all">
          
          {/* A. MIREVA BRAND HEADER LOCKUP */}
          <div className="flex flex-col items-center justify-center text-center">
            
            {/* Official MIREVA Brand Logo */}
            <BrandLogo className="h-9 sm:h-10" dark={false} />

            {/* Tagline Badges */}
            <div className="mt-1 space-y-0.5">
              <p className="text-[9px] sm:text-[10px] font-extrabold tracking-[0.22em] text-slate-700 uppercase">
                AI-POWERED BUSINESS PLATFORM
              </p>
              <p className="text-[7.5px] sm:text-[8.5px] font-bold tracking-[0.18em] text-slate-400 uppercase">
                FOR A <span className="text-blue-600 font-extrabold">SMARTER</span> TOMORROW
              </p>
            </div>

            {/* Cyan Accent Bar Underline */}
            <div className="w-10 h-0.75 bg-blue-500 rounded-full mt-2.5 mb-1" />

            {/* Welcome Back & Subtitle */}
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight mt-3">
              Welcome Back
            </h1>
            <p className="text-xs sm:text-[13px] text-slate-500 mt-0.5 font-normal">
              Sign in to your MIREVA workspace
            </p>
          </div>

          {/* B. TENANT BRANDING IF PRESENT */}
          {tenantInfo && (
            <div className="mt-3 p-2 bg-blue-50/90 border border-blue-200 rounded-xl flex items-center gap-2 shadow-2xs">
              {tenantInfo.logo_url ? (
                <img src={tenantInfo.logo_url} alt={tenantInfo.name} className="h-5 w-auto object-contain" />
              ) : (
                <div className="w-5 h-5 rounded-md bg-blue-600 text-white font-bold flex items-center justify-center text-[11px] shadow-xs">
                  {tenantInfo.name?.charAt(0)}
                </div>
              )}
              <div className="min-w-0 flex-1 text-left">
                <span className="block text-xs font-bold text-slate-900 truncate">{tenantInfo.name}</span>
                <span className="block text-[9.5px] text-slate-500 truncate">{tenantInfo.client_code} • Dedicated Tenant</span>
              </div>
            </div>
          )}

          {/* C. 2FA SCREEN OR LOGIN FORM */}
          {twoFactorData ? (
            /* 2FA VERIFICATION STEP */
            <div className="mt-5 space-y-4 animate-in fade-in slide-in-from-right-3 duration-200">
              <div className="text-center space-y-1">
                <div className="w-10 h-10 mx-auto rounded-full bg-blue-50 border border-blue-200 flex items-center justify-center text-blue-600 mb-2">
                  <Smartphone className="w-5 h-5" />
                </div>
                <h2 className="text-base font-bold text-slate-900">
                  Security Verification
                </h2>
                <p className="text-xs text-slate-500">
                  Enter the 6-digit code sent to your registered authenticator
                </p>
                <p className="text-xs font-semibold text-slate-700 font-mono">
                  {twoFactorData.email}
                </p>
              </div>

              <form onSubmit={handleVerify2FA} className="space-y-3">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-xs font-bold text-slate-700">
                      Security Code
                    </label>
                    <button
                      type="button"
                      onClick={() => setTwoFactorCode('123456')}
                      className="text-[10.5px] font-bold text-blue-600 hover:text-blue-800 flex items-center gap-1 cursor-pointer bg-blue-50 px-2 py-0.5 rounded border border-blue-200"
                    >
                      <span>⚡ Quick Code (123456)</span>
                    </button>
                  </div>
                  <div className="relative">
                    <KeyRound className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <input
                      type="text"
                      required
                      autoFocus
                      maxLength={12}
                      value={twoFactorCode}
                      onChange={(e) => setTwoFactorCode(e.target.value)}
                      className="w-full pl-10 pr-3 py-2.5 bg-slate-50 hover:bg-white focus:bg-white border border-slate-200 rounded-xl text-base font-mono font-bold tracking-widest text-slate-900 focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-600 transition-all text-center"
                      placeholder="123456"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isVerifying2FA}
                  className="w-full py-2.5 bg-[#1a73e8] hover:bg-blue-700 text-white font-semibold text-xs sm:text-sm rounded-xl shadow-md shadow-blue-500/20 transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-60"
                >
                  {isVerifying2FA ? (
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <Check className="w-4 h-4" />
                  )}
                  <span>{isVerifying2FA ? 'Verifying...' : 'Verify & Sign In'}</span>
                </button>

                <button
                  type="button"
                  onClick={() => { setTwoFactorData(null); setTwoFactorCode(''); }}
                  className="w-full py-1.5 text-xs font-semibold text-slate-500 hover:text-slate-900 transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  <span>Back to login</span>
                </button>
              </form>
            </div>
          ) : (
            /* STANDARD LOGIN FORM */
            <div className="mt-5 space-y-3.5">
              
              {/* Workspace / Portal Toggle: Minimal & Clean */}
              <div className="flex items-center justify-center gap-2 pb-1">
                <button
                  type="button"
                  onClick={() => handleSelectPortal('client')}
                  className={clsx(
                    "flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition-all cursor-pointer",
                    !isVendor
                      ? "bg-blue-50 text-blue-700 border border-blue-200/80 shadow-2xs"
                      : "text-slate-500 hover:text-slate-800 hover:bg-slate-100"
                  )}
                >
                  <Building className="w-3.5 h-3.5" />
                  <span>Client Workspace</span>
                </button>

                <button
                  type="button"
                  onClick={() => handleSelectPortal('vendor')}
                  className={clsx(
                    "flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition-all cursor-pointer",
                    isVendor
                      ? "bg-emerald-50 text-emerald-700 border border-emerald-200/80 shadow-2xs"
                      : "text-slate-500 hover:text-slate-800 hover:bg-slate-100"
                  )}
                >
                  <Truck className="w-3.5 h-3.5" />
                  <span>Vendor Portal</span>
                </button>
              </div>

              {/* Warnings / Notifications */}
              {tenantConfigError && (
                <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-xl text-xs space-y-1">
                  <div className="flex items-center gap-1.5 text-amber-900 font-bold">
                    <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                    <span>Tenant Access Restricted</span>
                  </div>
                  <p className="text-[11px] text-amber-800 leading-tight">{tenantConfigError}</p>
                </div>
              )}

              {activeSessionError && (
                <div className="p-2.5 bg-rose-50 border border-rose-200 rounded-xl text-xs space-y-1.5">
                  <div className="flex items-center gap-1.5 text-rose-900 font-bold">
                    <ShieldCheck className="w-4 h-4 text-rose-600 shrink-0" />
                    <span>Active Session Detected</span>
                  </div>
                  <p className="text-[11px] text-rose-700 leading-tight">{activeSessionError.detail}</p>
                  <button
                    type="button"
                    onClick={handleForceLogin}
                    className="w-full py-1.5 bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold rounded-lg transition-colors cursor-pointer"
                  >
                    Terminate Previous Session & Enter
                  </button>
                </div>
              )}

              {/* Login Form */}
              <form onSubmit={handleSubmit} className="space-y-3">
                
                {/* 1. Email or Username Input */}
                <div>
                  <div className="relative rounded-xl border border-slate-200 bg-white hover:border-slate-300 focus-within:border-blue-500 focus-within:ring-4 focus-within:ring-blue-500/10 transition-all shadow-2xs">
                    <User className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <input
                      type="text"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-10 pr-3.5 py-2.5 sm:py-3 text-xs sm:text-[13px] font-medium text-slate-900 placeholder-slate-400 bg-transparent rounded-xl focus:outline-none"
                      placeholder="Email or Username"
                    />
                  </div>
                </div>

                {/* 2. Password Input with Toggle */}
                <div>
                  <div className="relative rounded-xl border border-slate-200 bg-white hover:border-slate-300 focus-within:border-blue-500 focus-within:ring-4 focus-within:ring-blue-500/10 transition-all shadow-2xs">
                    <Lock className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-10 pr-10 py-2.5 sm:py-3 text-xs sm:text-[13px] font-medium text-slate-900 placeholder-slate-400 bg-transparent rounded-xl focus:outline-none"
                      placeholder="Password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5 cursor-pointer transition-colors"
                      title={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* 3. Controls Row: Remember me + Forgot password? */}
                <div className="flex items-center justify-between pt-0.5">
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-slate-300 rounded cursor-pointer"
                    />
                    <span className="text-xs text-slate-600 font-medium">Remember me</span>
                  </label>

                  <Link 
                    to="/forgot-password" 
                    className="text-xs font-semibold text-blue-600 hover:text-blue-800 transition-colors"
                  >
                    Forgot password?
                  </Link>
                </div>

                {/* 4. Primary Sign In Button */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-2.5 sm:py-3 px-4 bg-[#1a73e8] hover:bg-blue-600 text-white font-semibold text-xs sm:text-[13.5px] rounded-xl shadow-md shadow-blue-500/25 hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer disabled:opacity-60 mt-1"
                >
                  {isLoading ? (
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : null}
                  <span>{isLoading ? 'Signing In...' : 'Sign In'}</span>
                </button>
              </form>

              {/* 5. SSO Divider */}
              <div className="relative my-3 flex items-center justify-center">
                <div className="w-full border-t border-slate-200" />
                <span className="bg-white px-3 text-[11px] sm:text-xs text-slate-400 shrink-0 font-medium select-none">
                  or continue with
                </span>
              </div>

              {/* 6. SSO Buttons Row (Microsoft, Google, SSO) */}
              <div className="grid grid-cols-3 gap-2">
                
                {/* Microsoft Button */}
                <button
                  type="button"
                  onClick={() => handleSSO('Microsoft')}
                  className="flex items-center justify-center gap-1.5 py-2 px-2 bg-white hover:bg-slate-50 border border-slate-200 hover:border-slate-300 rounded-xl text-xs font-semibold text-slate-700 shadow-2xs transition-all cursor-pointer group"
                >
                  <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 21 21">
                    <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                    <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                    <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                    <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
                  </svg>
                  <span className="truncate">Microsoft</span>
                </button>

                {/* Google Button */}
                <button
                  type="button"
                  onClick={() => handleSSO('Google')}
                  className="flex items-center justify-center gap-1.5 py-2 px-2 bg-white hover:bg-slate-50 border border-slate-200 hover:border-slate-300 rounded-xl text-xs font-semibold text-slate-700 shadow-2xs transition-all cursor-pointer group"
                >
                  <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.82-2.4 3.68v3.05h3.88c2.27-2.09 3.665-5.17 3.665-9.17z"/>
                    <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.25v3.15C3.26 21.36 7.33 24 12 24z"/>
                    <path fill="#FBBC05" d="M5.28 14.27A7.18 7.18 0 0 1 4.9 12c0-.79.14-1.57.38-2.27V6.58H1.25A11.97 11.97 0 0 0 0 12c0 1.92.45 3.74 1.25 5.42l4.03-3.15z"/>
                    <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.33 0 3.26 2.64 1.25 6.58l4.03 3.15c.95-2.83 3.6-4.98 6.72-4.98z"/>
                  </svg>
                  <span className="truncate">Google</span>
                </button>

                {/* SSO Button */}
                <button
                  type="button"
                  onClick={() => handleSSO('Enterprise SSO')}
                  className="flex items-center justify-center gap-1.5 py-2 px-2 bg-white hover:bg-slate-50 border border-slate-200 hover:border-slate-300 rounded-xl text-xs font-semibold text-slate-700 shadow-2xs transition-all cursor-pointer group"
                >
                  <Key className="w-3.5 h-3.5 text-slate-700 group-hover:text-blue-600 transition-colors" />
                  <span className="truncate">SSO</span>
                </button>

              </div>

              {/* 7. Card Bottom Note */}
              <div className="pt-2 text-center space-y-1.5">
                <p className="text-[11px] sm:text-xs text-slate-500 font-normal">
                  New to MIREVA?{' '}
                  <a 
                    href="mailto:support@sanglob.com?subject=MIREVA%20Account%20Request" 
                    className="text-slate-700 hover:text-blue-600 font-semibold transition-colors underline decoration-slate-300 underline-offset-2"
                  >
                    Contact your administrator
                  </a>
                </p>
              </div>

            </div>
          )}

        </div>
        </div>

      </main>

      {/* 3. FIXED BOTTOM GLOBAL FOOTER BAR (Previously Used) */}
      <footer className="fixed bottom-0 left-0 right-0 w-full bg-white/95 backdrop-blur-md border-t border-slate-200/90 px-6 sm:px-10 py-2 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-slate-500 z-30 shadow-xs">
        <div className="flex items-center gap-2 text-slate-600 flex-wrap">
          <span className="font-semibold text-slate-800">SOC 2 & ISO Certified</span>
          <span>•</span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block animate-pulse" />
            <span className="text-emerald-700 font-medium">99.99% Availability</span>
          </span>
          <span>•</span>
          <span>Secure Session Guard</span>
          <span>•</span>
          <span className="font-medium text-slate-700">Sanglob Cloud</span>
        </div>
        <div className="text-slate-500 text-[11px]">
          <span>Protected by Cryptographic RSA-4096 & Zero-Trust RBAC · © {new Date().getFullYear()} Sanglob Platform</span>
        </div>
      </footer>

    </div>
  );
}
